/**
 * API Client Service
 * Configured Axios instance for making API requests to the Python backend
 */

import axios, { AxiosError } from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { fetchCSRFToken } from '../utils/csrf';

// Create axios instance with base configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true, // Important: Send cookies with requests for session management
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// Request interceptor - Add CSRF token and logging
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Log requests in development
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    // Add CSRF token for state-changing requests
    if (config.method && ['post', 'put', 'delete', 'patch'].includes(config.method.toLowerCase())) {
      try {
        const csrfToken = await fetchCSRFToken();
        config.headers['X-CSRFToken'] = csrfToken;
      } catch (error) {
        console.error('[API] Failed to fetch CSRF token:', error);
        // Continue without token - let backend reject if needed
      }
    }
    
    // Session cookies are automatically included via withCredentials: true
    
    return config;
  },
  (error: AxiosError) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
apiClient.interceptors.response.use(
  (response) => {
    // Log responses in development
    if (import.meta.env.DEV) {
      console.log(`[API Response] ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error: AxiosError) => {
    // Handle common error cases
    if (error.response) {
      const status = error.response.status;
      
      switch (status) {
        case 401:
          // Unauthorized - redirect to login
          console.warn('[API] Unauthorized - redirecting to login');
          // Redirect to login page with return URL
          const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
          const finalRedirectUrl = encodeURIComponent(window.location.origin + window.location.pathname);
          window.location.href = `${apiBaseUrl}/login?final_redirect_url=${finalRedirectUrl}`;
          break;
          
        case 403:
          console.error('[API] Forbidden - insufficient permissions');
          break;
          
        case 404:
          console.error('[API] Not Found');
          break;
          
        case 500:
        case 502:
        case 503:
          console.error('[API] Server Error');
          break;
          
        default:
          console.error(`[API] Error ${status}:`, error.response.data);
      }
    } else if (error.request) {
      console.error('[API] No response received:', error.message);
    } else {
      console.error('[API] Request setup error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;

/**
 * API Service Functions
 */

export const authApi = {
  /**
   * Get current user profile/session
   */
  getProfile: () => apiClient.get('/api/user/profile'),
  
  /**
   * Logout user
   */
  logout: () => apiClient.post('/logout'),
};

export const analysisApi = {
  /**
   * Get list of analyses with pagination
   */
  getAnalyses: (params?: { page?: number; per_page?: number }) => 
    apiClient.get('/lite/api/analysis', { params }),
  
  /**
   * Get specific analysis by ID
   */
  getAnalysis: (id: string) =>
    apiClient.get(`/lite/api/analysis/${id}`),
  
  /**
   * Submit communication for analysis (SSE stream)
   * Note: This returns a regular response, not SSE. SSE handled separately.
   */
  analyzeCommunication: (data: { response: string; context?: string }) =>
    apiClient.post('/lite/comms_checker', data),
};

export const analyticsApi = {
  /**
   * Get aggregate analytics data
   */
  getAggregate: (params?: { days?: number }) =>
    apiClient.get('/api/analytics/aggregate', { params }),
  
  /**
   * Create coach-ready share link
   */
  createShare: (data: { days: number }) =>
    apiClient.post('/api/analytics/share', data),
};

export const adminApi = {
  /**
   * List all organizations (admin only)
   */
  listOrganizations: () =>
    apiClient.get('/lite/admin/organizations'),

  /**
   * Invite a new user (admin only)
   */
  inviteUser: (data: { email: string; org_id: string }) =>
    apiClient.post('/lite/admin/invite', data),

  /**
   * List/search users (admin only)
   */
  listUsers: (params?: { q?: string; org_id?: string; page?: number; per_page?: number }) =>
    apiClient.get('/lite/admin/users', { params }),

  /**
   * Get a target user's analyses (admin only)
   */
  getUserAnalyses: (userId: string, params?: { page?: number; per_page?: number }) =>
    apiClient.get(`/lite/admin/users/${userId}/analyses`, { params }),

  /**
   * Get a target user's executive summary (admin only)
   */
  getUserExecutiveSummary: (userId: string) =>
    apiClient.get(`/lite/admin/users/${userId}/executive-summary`),

  /**
   * Generate/regenerate a target user's executive summary (admin only)
   */
  generateUserExecutiveSummary: (userId: string, data?: { force?: boolean }) =>
    apiClient.post(
      `/lite/admin/users/${userId}/executive-summary/generate`,
      data ?? {},
      { timeout: 90000 }
    ),

  /**
   * Get any analysis by ID (admin only)
   */
  getAnalysis: (analysisId: string) =>
    apiClient.get(`/lite/admin/analyses/${analysisId}`),
};

export const onboardingApi = {
  /**
   * Get user's onboarding status
   */
  getStatus: () =>
    apiClient.get('/lite/api/user/onboarding/status'),
  
  /**
   * Complete onboarding (includes all profile data + goals/tags)
   */
  complete: (data: {
    name: string;
    phone: string;
    address: string;
    primary_focus: string;
    stakeholder_tags: string[];
  }) =>
    apiClient.post('/lite/api/user/onboarding', data),
  
  /**
   * Save onboarding data (legacy - use complete instead)
   */
  saveOnboarding: (data: {
    primary_focus: string;
    stakeholder_tags: string[];
  }) => apiClient.post('/api/user/onboarding', data),
};

/**
 * Convenience exports for common API calls
 */
export const getAnalyses = async (page: number = 1, perPage: number = 10) => {
  const response = await analysisApi.getAnalyses({ page, per_page: perPage });
  return response.data;
};

export const getAnalysis = async (id: string) => {
  const response = await analysisApi.getAnalysis(id);
  return response.data;
};

